import React, { useCallback, useEffect, useState } from 'react';
import { BsArrowRepeat, BsClockHistory, BsDownload, BsPencil, BsPlus, BsTrash } from 'react-icons/bs';
import { apiFetch, parseJsonSafe, handleUnauthorized, API_URL } from '../utils/api';

const emptyForm = {
  id: null,
  name: '',
  cpu_threshold: 80,
  ram_threshold: 90,
  disk_threshold: 90,
  sustained_readings: 3,
  report_interval_minutes: 5,
  alert_offline: false,
  offline_after_minutes: 15,
  active: true
};

const STATUS_META = {
  ok: { label: 'Correcto', className: 'bg-success' },
  alert: { label: 'Umbral superado', className: 'bg-danger' },
  stale: { label: 'Sin datos', className: 'bg-warning text-dark' },
  paused: { label: 'Pausado', className: 'bg-secondary' },
  pending: { label: 'Esperando al agente', className: 'bg-light text-dark border' }
};

const METRIC_LABELS = { cpu: 'CPU', ram: 'RAM', disk: 'Disco', offline: 'Sin datos' };

const formatDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
};

const formatPct = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(0)}%`);

const formatDuration = (seconds) => {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s < 0) return '—';
  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ${mins % 60} min`;
  return `${Math.floor(hours / 24)} d ${hours % 24} h`;
};

const formatUptime = (seconds) => {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return '—';
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  return days ? `${days} d ${hours} h` : `${hours} h`;
};

// Barra de color: verde por debajo del umbral, roja al superarlo. Sin umbral = no se vigila.
const MetricBar = ({ value, threshold }) => {
  if (value === null || value === undefined) return <span className="text-muted">—</span>;
  const v = Number(value);
  const over = threshold ? v > Number(threshold) : false;
  return (
    <div>
      <div className="d-flex justify-content-between" style={{ fontSize: '0.75rem' }}>
        <span className={over ? 'text-danger fw-semibold' : ''}>{formatPct(v)}</span>
        {threshold ? <span className="text-muted">/{formatPct(threshold)}</span> : null}
      </div>
      <div className="progress" style={{ height: 4 }}>
        <div
          className={`progress-bar ${over ? 'bg-danger' : 'bg-success'}`}
          style={{ width: `${Math.min(Math.max(v, 0), 100)}%` }}
        />
      </div>
    </div>
  );
};

// /proc/stat es acumulado desde el arranque, así que un solo valor no dice nada del uso actual:
// hay que restar dos lecturas. El agente guarda la lectura anterior en disco y compara contra
// ella, de modo que la CPU que reporta es la MEDIA REAL de todo el intervalo (5 min por defecto)
// y no una muestra de un segundo, que con intervalos largos sería puro azar.
const buildInstallCommand = (server) => {
  const base = (API_URL || window.location.origin).replace(/\/$/, '');
  const url = `${base}/api/agent/metrics`;
  const every = Number(server.report_interval_minutes) || 5;
  const cron = every === 1 ? '* * * * *' : `*/${every} * * * *`;
  return `sudo tee /usr/local/bin/automationia-agent.sh > /dev/null <<'SCRIPT'
#!/bin/bash
# Agente de monitorizacion de Automationia - ${server.name}
URL="${url}"
TOKEN="${server.token}"
STATE=/var/lib/automationia-agent.stat

read -r _ a b c d e f g h _ < /proc/stat
idle=$((d + e)); total=$((a+b+c+d+e+f+g+h))

CPU=0
if [ -r "$STATE" ]; then
  read -r p_idle p_total < "$STATE"
  dt=$((total - p_total)); di=$((idle - p_idle))
  # dt<=0 significa que el servidor se reinicio (los contadores vuelven a cero): se descarta.
  if [ "$dt" -gt 0 ] && [ "$di" -ge 0 ]; then
    CPU=$(( (100 * (dt - di)) / dt ))
  fi
else
  # Primera ejecucion: aun no hay con que comparar, asi que se toma una muestra de 1 segundo.
  sleep 1
  read -r _ a b c d e f g h _ < /proc/stat
  idle2=$((d + e)); total2=$((a+b+c+d+e+f+g+h))
  dt=$((total2 - total)); di=$((idle2 - idle))
  [ "$dt" -gt 0 ] && CPU=$(( (100 * (dt - di)) / dt ))
  idle=$idle2; total=$total2
fi
echo "$idle $total" > "$STATE"

RAM=$(free | awk '/^Mem:/ {printf "%.1f", ($2 - $7) / $2 * 100}')
read -r DISK MOUNT <<< "$(df -P -x tmpfs -x devtmpfs -x squashfs -x overlay 2>/dev/null | awk 'NR>1 {gsub("%","",$5); if ($5+0 >= max) {max=$5+0; mnt=$6}} END {print max, mnt}')"
LOAD=$(cut -d' ' -f1 /proc/loadavg)
UP=$(cut -d. -f1 /proc/uptime)
HOST=$(hostname)

curl -sS -m 20 -X POST "$URL" \\
  -H 'Content-Type: application/json' \\
  -H "X-Server-Token: $TOKEN" \\
  -d "{\\"cpu\\":$CPU,\\"ram\\":$RAM,\\"disk\\":$DISK,\\"disk_mount\\":\\"$MOUNT\\",\\"load\\":$LOAD,\\"uptime\\":$UP,\\"hostname\\":\\"$HOST\\"}" > /dev/null
SCRIPT
# Solo root puede leer el script: contiene el token, y en un servidor compartido un PHP
# comprometido de cualquier web podria leerlo si quedara con permisos 755.
sudo chmod 700 /usr/local/bin/automationia-agent.sh
# La tarea va en /etc/cron.d (fichero propio) y NO en el crontab de root: asi la instalacion
# no reescribe el crontab y no puede llevarse por delante backups u otras tareas existentes.
echo "${cron} root /usr/local/bin/automationia-agent.sh >/dev/null 2>&1" | sudo tee /etc/cron.d/automationia-agent > /dev/null
sudo chmod 644 /etc/cron.d/automationia-agent
# Migracion desde la version anterior del agente (usaba el crontab de root): solo reescribe el
# crontab si se leyo bien Y contiene la entrada antigua; nunca puede dejarlo vacio.
sudo crontab -l 2>/dev/null > /tmp/aia-cron.$$; grep -q automationia-agent /tmp/aia-cron.$$ && grep -v automationia-agent /tmp/aia-cron.$$ | sudo crontab -; rm -f /tmp/aia-cron.$$
sudo /usr/local/bin/automationia-agent.sh && echo "OK: agente instalado, enviando cada ${every} min"`;
};

const ServersPanel = () => {
  const token = localStorage.getItem('token');
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [installServer, setInstallServer] = useState(null);
  const [copied, setCopied] = useState(false);

  const [historyServer, setHistoryServer] = useState(null);
  const [history, setHistory] = useState(null);

  const fetchServers = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await apiFetch('/api/monitoring/servers', { headers: authHeaders() });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'Error obteniendo los servidores');
      setServers(Array.isArray(data.items) ? data.items : []);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchServers(); }, [fetchServers]);
  useEffect(() => {
    const id = setInterval(() => fetchServers({ silent: true }), 60000);
    return () => clearInterval(id);
  }, [fetchServers]);

  const openNew = () => { setForm(emptyForm); setModalOpen(true); };

  const openEdit = (s) => {
    setForm({
      id: s.id,
      name: s.name || '',
      cpu_threshold: s.cpu_threshold ?? '',
      ram_threshold: s.ram_threshold ?? '',
      disk_threshold: s.disk_threshold ?? '',
      sustained_readings: s.sustained_readings ?? 3,
      report_interval_minutes: s.report_interval_minutes ?? 5,
      alert_offline: Boolean(s.alert_offline),
      offline_after_minutes: s.offline_after_minutes ?? 15,
      active: Boolean(s.active)
    });
    setModalOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await apiFetch(form.id ? `/api/monitoring/servers/${form.id}` : '/api/monitoring/servers', {
        method: form.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(form)
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'Error guardando');
      setModalOpen(false);
      await fetchServers({ silent: true });
      // Un servidor recién creado no sirve de nada hasta instalar el agente: lo enseñamos ya.
      if (!form.id) { setCopied(false); setInstallServer(data); }
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`¿Eliminar "${s.name}"? Se borrará también su histórico de métricas.`)) return;
    try {
      const res = await apiFetch(`/api/monitoring/servers/${s.id}`, { method: 'DELETE', headers: authHeaders() });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((await parseJsonSafe(res)).message || 'Error eliminando');
      await fetchServers({ silent: true });
    } catch (err) {
      alert(err.message);
    }
  };

  const rotateToken = async (s) => {
    if (!window.confirm('Al rotar el token, el agente instalado dejará de funcionar hasta que vuelvas a instalarlo con el token nuevo. ¿Continuar?')) return;
    try {
      const res = await apiFetch(`/api/monitoring/servers/${s.id}/rotate-token`, { method: 'POST', headers: authHeaders() });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'Error rotando el token');
      await fetchServers({ silent: true });
      setCopied(false);
      setInstallServer(data);
    } catch (err) {
      alert(err.message);
    }
  };

  const openHistory = async (s) => {
    setHistoryServer(s);
    setHistory(null);
    try {
      const res = await apiFetch(`/api/monitoring/servers/${s.id}/history`, { headers: authHeaders() });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'Error obteniendo el historial');
      setHistory(data);
    } catch (err) {
      alert(err.message);
      setHistoryServer(null);
    }
  };

  const copyCommand = async (s) => {
    try {
      await navigator.clipboard.writeText(buildInstallCommand(s));
      setCopied(true);
    } catch (_e) {
      setCopied(false);
      alert('No se pudo copiar automáticamente. Selecciona el texto y cópialo a mano.');
    }
  };

  const alertCount = servers.filter((s) => s.status === 'alert').length;
  const staleCount = servers.filter((s) => s.status === 'stale').length;

  return (
    <>
      <div className="d-flex justify-content-end mb-3">
        <button className="btn btn-primary btn-sm" onClick={openNew}>
          <BsPlus className="me-1" /> Nuevo servidor
        </button>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {(alertCount > 0 || staleCount > 0) && (
        <div className="alert alert-warning py-2">
          {alertCount > 0 && <span>{alertCount} servidor(es) con algún umbral superado. </span>}
          {staleCount > 0 && (
            <span>
              {staleCount} servidor(es) <strong>sin datos</strong>: el agente ha dejado de reportar,
              así que no sabemos cómo están.
            </span>
          )}
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-body">
          {loading ? (
            <div className="text-muted py-3">Cargando…</div>
          ) : !servers.length ? (
            <div className="text-muted py-3">
              Todavía no hay ningún servidor. Pulsa «Nuevo servidor»: al crearlo te daremos un
              comando para instalar el agente, que es quien envía la CPU, la RAM y el disco.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Estado</th>
                    <th style={{ minWidth: 90 }}>CPU</th>
                    <th style={{ minWidth: 90 }}>RAM</th>
                    <th style={{ minWidth: 90 }}>Disco</th>
                    <th>Encendido</th>
                    <th>Último dato</th>
                    <th className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {servers.map((s) => {
                    const meta = STATUS_META[s.status] || STATUS_META.pending;
                    return (
                      <tr key={s.id}>
                        <td>
                          <div className="fw-semibold">{s.name}</div>
                          {s.last_hostname && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{s.last_hostname}</div>}
                        </td>
                        <td><span className={`badge ${meta.className}`}>{meta.label}</span></td>
                        <td><MetricBar value={s.last_cpu} threshold={s.cpu_threshold} /></td>
                        <td><MetricBar value={s.last_ram} threshold={s.ram_threshold} /></td>
                        <td>
                          <MetricBar value={s.last_disk} threshold={s.disk_threshold} />
                          {s.last_disk_mount && <div className="text-muted" style={{ fontSize: '0.7rem' }}>{s.last_disk_mount}</div>}
                        </td>
                        <td>{formatUptime(s.last_uptime_seconds)}</td>
                        <td>{formatDateTime(s.last_seen_at)}</td>
                        <td className="text-end text-nowrap">
                          <button className="btn btn-outline-primary btn-sm me-1" title="Instalar agente" onClick={() => { setCopied(false); setInstallServer(s); }}>
                            <BsDownload />
                          </button>
                          <button className="btn btn-outline-secondary btn-sm me-1" title="Historial" onClick={() => openHistory(s)}>
                            <BsClockHistory />
                          </button>
                          <button className="btn btn-outline-secondary btn-sm me-1" title="Editar" onClick={() => openEdit(s)}>
                            <BsPencil />
                          </button>
                          <button className="btn btn-outline-secondary btn-sm me-1" title="Rotar token" onClick={() => rotateToken(s)}>
                            <BsArrowRepeat />
                          </button>
                          <button className="btn btn-outline-danger btn-sm" title="Eliminar" onClick={() => remove(s)}>
                            <BsTrash />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{form.id ? 'Editar servidor' : 'Nuevo servidor'}</h5>
                <button type="button" className="btn-close" onClick={() => setModalOpen(false)} />
              </div>
              <form onSubmit={submit}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label small">Nombre *</label>
                      <input
                        className="form-control"
                        placeholder="Servidor ERP (Dolibarr)"
                        value={form.name}
                        onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                        required
                      />
                    </div>

                    <div className="col-12"><hr className="my-1" /><span className="text-muted">Avisar cuando se supere…</span></div>

                    <div className="col-md-4">
                      <label className="form-label small">CPU (%)</label>
                      <input
                        className="form-control" type="number" min="1" max="100" placeholder="Sin vigilar"
                        value={form.cpu_threshold}
                        onChange={(e) => setForm((p) => ({ ...p, cpu_threshold: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small">RAM (%)</label>
                      <input
                        className="form-control" type="number" min="1" max="100" placeholder="Sin vigilar"
                        value={form.ram_threshold}
                        onChange={(e) => setForm((p) => ({ ...p, ram_threshold: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small">Disco (%)</label>
                      <input
                        className="form-control" type="number" min="1" max="100" placeholder="Sin vigilar"
                        value={form.disk_threshold}
                        onChange={(e) => setForm((p) => ({ ...p, disk_threshold: e.target.value }))}
                      />
                    </div>
                    <div className="col-12">
                      <div className="form-text mt-0">Déjalo vacío para no vigilar esa métrica.</div>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label small">El agente envía cada</label>
                      <select
                        className="form-select"
                        value={form.report_interval_minutes}
                        onChange={(e) => {
                          const every = Number(e.target.value);
                          // "Sin datos" debe ser siempre mayor que dos envíos, o un envío que
                          // llegue tarde daría una falsa alarma de servidor caído.
                          setForm((p) => ({
                            ...p,
                            report_interval_minutes: every,
                            offline_after_minutes: Math.max(p.offline_after_minutes, every * 3)
                          }));
                        }}
                      >
                        <option value={1}>1 minuto</option>
                        <option value={5}>5 minutos</option>
                        <option value={10}>10 minutos</option>
                        <option value={15}>15 minutos</option>
                      </select>
                      <div className="form-text">Cada envío informa de la media del intervalo.</div>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label small">Avisar solo si se mantiene</label>
                      <select
                        className="form-select"
                        value={form.sustained_readings}
                        onChange={(e) => setForm((p) => ({ ...p, sustained_readings: Number(e.target.value) }))}
                      >
                        {[1, 2, 3, 6].map((n) => (
                          <option key={n} value={n}>
                            {n === 1
                              ? `1 lectura (~${form.report_interval_minutes} min)`
                              : `${n} lecturas seguidas (~${n * form.report_interval_minutes} min)`}
                          </option>
                        ))}
                      </select>
                      <div className="form-text">
                        Solo avisamos si el problema persiste durante ese tiempo.
                      </div>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label small">Considerar «sin datos» tras</label>
                      <select
                        className="form-select"
                        value={form.offline_after_minutes}
                        onChange={(e) => setForm((p) => ({ ...p, offline_after_minutes: Number(e.target.value) }))}
                      >
                        {[15, 30, 60, 120]
                          .filter((m) => m > form.report_interval_minutes * 2)
                          .map((m) => (
                            <option key={m} value={m}>{m} minutos sin reportar</option>
                          ))}
                      </select>
                      <div className="form-check mt-2">
                        <input
                          className="form-check-input" type="checkbox" id="srv-offline"
                          checked={form.alert_offline}
                          onChange={(e) => setForm((p) => ({ ...p, alert_offline: e.target.checked }))}
                        />
                        <label className="form-check-label small" htmlFor="srv-offline">
                          Avisarme si deja de reportar (probablemente el servidor esté caído)
                        </label>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-check">
                        <input
                          className="form-check-input" type="checkbox" id="srv-active"
                          checked={form.active}
                          onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
                        />
                        <label className="form-check-label" htmlFor="srv-active">
                          Activo (se vigilan los umbrales)
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
                  <button className="btn btn-primary" type="submit" disabled={saving}>
                    {saving ? 'Guardando…' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {installServer && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Instalar el agente en «{installServer.name}»</h5>
                <button type="button" className="btn-close" onClick={() => setInstallServer(null)} />
              </div>
              <div className="modal-body">
                <p className="mb-2">
                  La CPU de un servidor no se puede medir desde fuera, así que hace falta un pequeño
                  agente dentro de la máquina. Conéctate por SSH y pega este bloque entero: crea el
                  script, lo programa cada minuto y envía la primera lectura.
                </p>
                <div className="alert alert-warning py-2 small">
                  El comando contiene el <strong>token</strong> de este servidor. Cualquiera que lo
                  tenga puede enviar métricas falsas en su nombre, así que no lo compartas. Si se
                  filtra, usa el botón de rotar token.
                </div>
                <pre
                  className="bg-dark text-light p-3 rounded"
                  style={{ maxHeight: 340, overflow: 'auto', fontSize: '0.75rem' }}
                >{buildInstallCommand(installServer)}</pre>
                <div className="form-text">
                  Requisitos: Linux con <code>bash</code>, <code>curl</code> y <code>cron</code>, pegado
                  como root o con sudo. Funciona en Ubuntu/Debian (Plesk, HestiaCP, CloudPanel…).
                  Para desinstalarlo:{' '}
                  <code>sudo rm -f /etc/cron.d/automationia-agent /usr/local/bin/automationia-agent.sh /var/lib/automationia-agent.stat</code>
                </div>
              </div>
              <div className="modal-footer justify-content-between">
                <button className={`btn ${copied ? 'btn-success' : 'btn-outline-primary'}`} onClick={() => copyCommand(installServer)}>
                  {copied ? '✓ Copiado' : 'Copiar comando'}
                </button>
                <button className="btn btn-outline-secondary" onClick={() => { setInstallServer(null); fetchServers({ silent: true }); }}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {historyServer && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Historial · {historyServer.name}</h5>
                <button type="button" className="btn-close" onClick={() => setHistoryServer(null)} />
              </div>
              <div className="modal-body">
                {!history ? (
                  <div className="text-muted">Cargando…</div>
                ) : (
                  <>
                    <h6>Avisos</h6>
                    {!history.incidents.length ? (
                      <p className="text-muted">Ningún umbral superado hasta ahora.</p>
                    ) : (
                      <table className="table table-sm">
                        <thead>
                          <tr><th>Métrica</th><th>Inicio</th><th>Fin</th><th>Duración</th><th className="text-end">Pico</th></tr>
                        </thead>
                        <tbody>
                          {history.incidents.map((i) => (
                            <tr key={i.id}>
                              <td>{METRIC_LABELS[i.metric] || i.metric}</td>
                              <td>{formatDateTime(i.started_at)}</td>
                              <td>{i.resolved_at ? formatDateTime(i.resolved_at) : <span className="badge bg-danger">En curso</span>}</td>
                              <td>{formatDuration(i.duration_seconds)}</td>
                              <td className="text-end">{formatPct(i.peak)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    <h6 className="mt-4">Últimas lecturas</h6>
                    {!history.metrics.length ? (
                      <p className="text-muted">
                        El agente todavía no ha enviado ninguna lectura. Comprueba que lo instalaste
                        y que el servidor puede salir a internet.
                      </p>
                    ) : (
                      <table className="table table-sm table-striped">
                        <thead>
                          <tr><th>Fecha</th><th className="text-end">CPU</th><th className="text-end">RAM</th><th className="text-end">Disco</th><th className="text-end">Carga</th></tr>
                        </thead>
                        <tbody>
                          {history.metrics.map((m, i) => (
                            <tr key={`${m.measured_at}-${i}`}>
                              <td>{formatDateTime(m.measured_at)}</td>
                              <td className="text-end">{formatPct(m.cpu)}</td>
                              <td className="text-end">{formatPct(m.ram)}</td>
                              <td className="text-end">{formatPct(m.disk)}</td>
                              <td className="text-end">{m.load1 ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setHistoryServer(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ServersPanel;
